// Canonical URLs for an article's source locators. Stored DOIs are NOT URLs
// (bare `10.x/…`, `doi:`-prefixed, or already a doi.org URL) and stored
// external links may lack a protocol — rendered raw into an href or
// window.open, both resolve RELATIVE to the current page (the podcast info
// panel's blank-page bug, #bugs 2026-08-24).

const DOI_URL_PREFIX = /^https?:\/\/(?:dx\.)?doi\.org\//i;

/** Bare registrant/suffix form of a stored DOI, or null when nothing usable remains. */
export function bareDoi(doi: string): string | null {
  const bare = doi.trim().replace(DOI_URL_PREFIX, "").replace(/^doi:/i, "");
  return bare === "" ? null : bare;
}

/** Resolver URL (`https://doi.org/…`) for a stored DOI, or null when it is empty. */
export function doiUrl(doi: string): string | null {
  const bare = bareDoi(doi);
  return bare === null ? null : `https://doi.org/${bare}`;
}

/**
 * Where "View original" goes: the publisher's page when the work stores one,
 * else the DOI resolver. Null when the work carries neither.
 */
export function originalSourceUrl(work: {
  externalLink: string | null;
  doi: string | null;
}): string | null {
  const external =
    work.externalLink === null ? null : externalSourceUrl(work.externalLink);
  if (external !== null) return external;
  return work.doi === null ? null : doiUrl(work.doi);
}

const HAS_HOST = /^https?:\/\/[^/\s?#.]+(?:\.[^/\s?#.]+)+\.?(?:[/?#]|$)/i;

/**
 * A stored external link as an absolute URL, or null when it names no page:
 * empty, or a stub such as `https://` with no host. A caller that falls back
 * to the DOI must not trade a working resolver for a dead link.
 */
export function externalSourceUrl(link: string): string | null {
  const trimmed = link.trim();
  if (trimmed === "") return null;
  const absolute =
    trimmed.startsWith("http://") || trimmed.startsWith("https://")
      ? trimmed
      : `https://${trimmed}`;
  // A host with a dot before any path. A regex rather than `URL.canParse`,
  // which the iOS 15 WebView the native shell still supports does not have.
  return HAS_HOST.test(absolute) ? absolute : null;
}
